import { generateUUIDv4 } from '@/util/utils'
import { BusEvent } from '@/utils'
import { LoadingOutlined } from '@ant-design/icons'
import { useCallback, useEffect, useRef, useState } from 'react'
import { showMessage } from '../antd-comp/message'
import ChatInput from './ChatInput'
import { useChatStore } from './chatStore'
import MessagePanne from './MessagePanne'
import { getSpecificPluginResultMap } from './utils'

export default function ChatComponent({
  clear = false,
  questions = [],
  form = false,
  inputForm = null,
  chatUrl,
  assistantId,
  loadMore = () => {},
  conversation = '',
  prompt = '',
  useName = '',
  conversationId = '',
  disabled = false,
  recommendVisible = true,
  tips = '',
  modelName = '',
  model_icon = '',
  deep_think_mode = 0,
  userName = '',
  supportSearch = true,
  thinking_mode = 1,
  tool_ids = [],
  quickQuestions = '',
  tool_enable = false,
  showMcpOutput = false,
  logo = '',
  onInputClick = () => {}
}) {
  // 从聊天状态管理中获取文件上传加载状态
  const { upIsLoading, setUpIsLoading } = useChatStore()

  /**
   * 组件初始化时重置文件上传状态
   */
  useEffect(() => {
    console.log('重置文件上传的loading状态', upIsLoading)
    setUpIsLoading(false)
  }, [])

  const pluginStatusMap = getSpecificPluginResultMap(tool_ids, tool_enable)
  // 判断是否为通义万相wan2.6模型
  const isWanxiangModel = modelName === '通义万相wan2.6'
  const [dragOver, setDragOver] = useState(false)
  // 使用进入/离开计数避免子元素触发闪烁
  const dragCounterRef = useRef(0)

  const onDragEnter = useCallback(
    (e: React.DragEvent) => {
      // 万相模型不支持拖拽上传
      if (isWanxiangModel) return
      e.preventDefault()
      e.stopPropagation()
      dragCounterRef.current += 1
      if (!dragOver) setDragOver(true)
    },
    [dragOver, isWanxiangModel]
  )

  const onDragOver = useCallback(
    (e: React.DragEvent) => {
      // 万相模型不支持拖拽上传
      if (isWanxiangModel) return
      e.preventDefault()
      e.stopPropagation()
      // 提示复制效果，避免默认打开文件
      e.dataTransfer.dropEffect = 'copy'
      if (!dragOver) setDragOver(true)
    },
    [dragOver, isWanxiangModel]
  )

  const onDragLeave = useCallback(
    (e: React.DragEvent) => {
      // 万相模型不支持拖拽上传
      if (isWanxiangModel) return
      e.preventDefault()
      e.stopPropagation()
      dragCounterRef.current -= 1
      if (dragCounterRef.current <= 0) {
        dragCounterRef.current = 0
        setDragOver(false)
      }
    },
    [isWanxiangModel]
  )

  const onDrop = useCallback(
    (e: React.DragEvent) => {
      // 万相模型不支持拖拽上传
      if (isWanxiangModel) return
      e.preventDefault()
      e.stopPropagation()
      dragCounterRef.current = 0
      setDragOver(false)
      BusEvent.emit('chartFileDrop', e)
    },
    [isWanxiangModel]
  )

  // 推荐问题 e = 2 重新生成 : 隐藏
  const [recommendedHid, setRecommendedHid] = useState(false)

  /**
   * 处理推荐问题的重新生成和显示切换
   * @param e - 操作类型：2表示重新生成，其他表示切换显示状态
   */
  const regenerate = e => {
    if (disabled) {
      showMessage({
        content: tips,
        type: 'warning'
      })
      return
    }
    if (e === 2) {
      // 重新生成：如果当前未显示则显示推荐问题
      if (!recommendedHid) {
        setRecommendedHid(true)
      }
    } else {
      // 切换显示状态
      setRecommendedHid(!recommendedHid)
    }
  }

  /**
   * 当推荐问题列表变化时，如果有问题则自动显示
   */
  useEffect(() => {
    if (questions && questions?.length > 0) {
      setRecommendedHid(true)
    }
  }, [questions])

  // 临时会话ID，用于新会话
  let [alterConversationId, setAlterConversationId] = useState(generateUUIDv4())

  // 聊天状态：用于控制会话中的功能按钮显示（重发、暂停、编辑等）
  const [chatting, setChatting] = useState('')

  // 实际展示推荐问题的状态：需要同时满足有列表、允许展示且处于展开态
  const recommendedShown = recommendVisible && recommendedHid && Array.isArray(questions) && questions.length > 0

  /**
   * 会话状态变化时控制推荐问题显示
   * 当开始聊天或会话锁定时，自动收起推荐问题列表
   */
  useEffect(() => {
    if (chatting === 'locked' || chatting === '') {
      setRecommendedHid(false)
    }
  }, [chatting])
  useEffect(() => {
    const fun = () => setDragOver(false)
    BusEvent.on('chartFileDropComplete', fun)
    return () => {
      BusEvent.off('chartFileDropComplete', fun)
    }
  }, [])

  return (
    <div
      className="relative h-full"
      onDragEnter={onDragEnter}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      {dragOver && (
        <div className="chat-file-upload-container flex flex-col items-center justify-center">
          <div className="flex flex-col items-center justify-center">
            <div className="common-upload-main-text">附件拖拽到此处可上传</div>
            <div className="common-upload-sub-text mx-[32px] text-center">
              上传图片(大小不超过5MB，支持.jpg/.jpeg/.png)；
              <br />
              支持上传的文件(大小不能超过10MB),接受.pdf(支持解析50页内容),
              .docx,.doc,.ppt,.pptx,.wav,.mp3,.m4a,.ogg,.opus,.amr,.aac,.xlsx,.xls,.csv格式文件
            </div>
          </div>
        </div>
      )}

      {upIsLoading && (
        <div className="absolute inset-0 z-50 flex cursor-not-allowed items-center justify-center bg-white bg-opacity-50">
          <div className="chat-file-loading-box">
            <LoadingOutlined style={{ fontSize: 24, color: '#fff', marginBottom: 8 }} spin />
            <div style={{ color: '#fff' }}>上传中</div>
          </div>
        </div>
      )}

      {/* 消息面板组件：显示聊天消息列表 */}
      <MessagePanne
        chatting={chatting}
        useName={useName}
        recommendedHid={recommendedHid}
        recommendedShown={recommendedShown}
        loadMore={loadMore}
        conversationId={conversationId ? conversationId : alterConversationId}
        conversation={conversation}
        recommendVisible={recommendVisible}
        showMcpOutput={showMcpOutput}
        logo={logo}
      ></MessagePanne>

      {/* 聊天输入组件：处理用户输入和发送消息 */}
      <ChatInput
        onInputClick={onInputClick}
        quickQuestions={quickQuestions}
        recommendVisible={recommendVisible}
        chattingFun={e => {
          setChatting(e)
        }}
        disabled={disabled}
        tips={tips}
        userName={userName}
        recommendedHid={recommendedHid}
        regenerate={regenerate}
        conversationId={conversationId}
        alterConversationId={alterConversationId}
        clear={clear}
        prompt={prompt}
        questions={questions}
        form={form}
        chatUrl={chatUrl}
        assistantId={assistantId}
        inputForm={inputForm}
        conversation={conversation}
        modelName={modelName}
        model_icon={model_icon}
        deep_think_mode={deep_think_mode}
        supportSearch={supportSearch}
        thinking_mode={thinking_mode}
        tool_ids={tool_ids}
        tool_enable={tool_enable}
      ></ChatInput>
    </div>
  )
}
